/**
 * Portfolio Subscriptions API Client — Chunk 191: Social Copy-Trading Subscriptions
 */
import apiClient from './client';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface SubscribeRequest {
  notifyOnRebalance?: boolean;
  notifyOnNewHolding?: boolean;
  notifyThresholdPct?: number;
}

export interface PortfolioSubscription {
  id: string;
  discoveryId: string;
  displayName: string;
  notifyOnRebalance: boolean;
  notifyOnNewHolding: boolean;
  notifyThresholdPct: number;
  subscribedAt: string;
  followCount: number;
  latestSnapshot: PortfolioSnapshot | null;
}

export interface PortfolioSnapshot {
  allocations?: Array<{ symbol: string; percentage?: number }>;
  holdings?: Array<{ symbol: string; allocationPct?: number }>;
  [key: string]: unknown;
}

export interface SubscriptionsResponse {
  subscriptions: PortfolioSubscription[];
  total: number;
}

export interface SubscribeResponse {
  id: string;
  discoveryId: string;
  notifyOnRebalance: boolean;
  notifyOnNewHolding: boolean;
  notifyThresholdPct: number;
  subscribedAt: string;
}

export interface SubscriptionStatusResponse {
  subscribed: boolean;
  subscription: SubscribeResponse | null;
}

// ─── API Functions ──────────────────────────────────────────────────────────────

/**
 * Subscribe to a portfolio on the community leaderboard.
 * Creates or updates the subscription with notification settings.
 */
export async function subscribeToPortfolio(
  discoveryId: string,
  opts: SubscribeRequest = {}
): Promise<SubscribeResponse> {
  const res = await apiClient.post<SubscribeResponse>(
    `/analytics/discovery/${discoveryId}/subscribe`,
    {
      notifyOnRebalance: opts.notifyOnRebalance ?? true,
      notifyOnNewHolding: opts.notifyOnNewHolding ?? true,
      notifyThresholdPct: opts.notifyThresholdPct ?? 5.0,
    }
  );
  return res.data;
}

/**
 * Unsubscribe from a portfolio.
 */
export async function unsubscribeFromPortfolio(discoveryId: string): Promise<void> {
  await apiClient.delete(`/analytics/discovery/${discoveryId}/subscribe`);
}

/**
 * Get all portfolios the current user is following.
 */
export async function getSubscriptions(): Promise<SubscriptionsResponse> {
  const res = await apiClient.get<SubscriptionsResponse>('/analytics/discovery/subscriptions');
  return res.data;
}

/**
 * Check whether the current user is following a specific portfolio.
 */
export async function getSubscriptionStatus(
  discoveryId: string
): Promise<SubscriptionStatusResponse> {
  const res = await apiClient.get<SubscriptionStatusResponse>(
    `/analytics/discovery/${discoveryId}/subscription-status`
  );
  return res.data;
}
